import _ from 'lodash'
import { useCallback, useEffect, useRef } from 'react'

const selector = 'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'

export function useTanTableNavigation({ tableRef }) {
    const cellElements = useRef<Element[]>([])
    const rowElements = useRef<Element[]>([])
    const lastFocusedRowIndex = useRef<number>(-1)

    const updateNavList = useCallback(
        _.debounce(() => {
            if (!tableRef.current) return

            const navItems = tableRef.current.querySelectorAll('[data-table-nav="cell"]')
            const focusableElements = Array.from(navItems).filter(item => {

                const interactiveEl = (item as HTMLElement).querySelector(selector)

                // Verify that the element is visible
                return interactiveEl &&
                       !interactiveEl.hasAttribute('aria-hidden') &&
                       getComputedStyle(interactiveEl).display !== 'none' &&
                       getComputedStyle(interactiveEl).visibility !== 'hidden'
            })

            const rowItems = tableRef.current.querySelectorAll('[data-table-nav="row"]')

            cellElements.current = focusableElements as Element[]
            rowElements.current = rowItems
        }, 100),
        [tableRef]
    )

    const focusHeaderOrFooter = (shiftKey: boolean) => {
        const targetSelector = shiftKey ? '[data-table-header]' : '[data-table-footer]'
        const navigationElement = tableRef.current?.querySelector(targetSelector) as HTMLElement
        navigationElement?.focus()
    }

    const handleHeaderFooterNavigation = (e: KeyboardEvent, target: HTMLElement): boolean => {
        const isHeaderToRowNav = target.hasAttribute('data-table-header') && !e.shiftKey
        const isFooterToRowNav = target.hasAttribute('data-table-footer') && e.shiftKey

        if ((isHeaderToRowNav || isFooterToRowNav) && lastFocusedRowIndex.current >= 0) {
            const targetRow = rowElements.current[lastFocusedRowIndex.current] as HTMLElement

            // Get all cells for this row
            const rowCells = cellElements.current.filter(
                cell => cell.getAttribute('row-index') === lastFocusedRowIndex.current.toString()
            )

            if (rowCells.length > 0) {
                e.preventDefault()

                // If coming from footer (shift+tab), focus last cell in row
                // If coming from header (tab), focus first cell in row
                const targetCell = isFooterToRowNav
                    ? rowCells[rowCells.length - 1]
                    : rowCells[0]

                const focusableElement = targetCell.querySelector(selector) as HTMLElement
                if (focusableElement) {
                    focusableElement.focus()
                    return true
                }
            }

            // Fallback to focusing the row if no cells could be focused
            if (targetRow) {
                e.preventDefault()
                targetRow.focus()
                return true
            }
        }
        return false
    }

    const handleCellNavigation = (e: KeyboardEvent, currentNavItem: Element, currentCellIndex: number) => {
        e.preventDefault()
        const nextIndex = e.shiftKey ? currentCellIndex - 1 : currentCellIndex + 1
        const nextElement = cellElements.current[nextIndex] as HTMLElement

        const currentRowIndex = currentNavItem.getAttribute('row-index')
        const nextRowIndex = nextElement?.getAttribute('row-index')

        if (!nextElement || (nextRowIndex !== currentRowIndex)) {
            focusHeaderOrFooter(e.shiftKey)
            lastFocusedRowIndex.current = parseInt(currentRowIndex)
            return
        }

        const focusableElement = nextElement.querySelector(selector) as HTMLElement
        focusableElement?.focus()
    }

    const handleRowNavigation = (e: KeyboardEvent, currentRowIndex: number) => {
        e.preventDefault()

        // Check if we are moving up or moving down and there are no focusable cells
        if (e.shiftKey || !cellElements.current.length) {
            focusHeaderOrFooter(e.shiftKey)
        } else {
            // Find the first cell that belongs to the current row
            const firstCellInRow = cellElements.current.find(
                cell => cell.getAttribute('row-index') === currentRowIndex.toString()
            ) as HTMLElement

            if (firstCellInRow) {
                const focusableElement = firstCellInRow.querySelector(selector) as HTMLElement
                focusableElement?.focus()
            }
        }

        lastFocusedRowIndex.current = currentRowIndex
    }

    // Simplificar la función de navegación entre filas
    const navigateToRow = (rowIndex: number) => {
        const targetRow = rowElements.current[rowIndex] as HTMLElement
        if (targetRow) {
            targetRow.focus()
            lastFocusedRowIndex.current = rowIndex
        }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        const target = document.activeElement as HTMLElement
        const currentNavItem = target.closest('[data-table-nav]') || target

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const currentRowIndex = Array.from(rowElements.current).findIndex(
                element => element.contains(currentNavItem) || element === currentNavItem
            )

            // We are not in a row
            if (currentRowIndex === -1) return

            e.preventDefault()
            const isMovingUp = e.key === 'ArrowUp'
            const isJumpToEdge = e.ctrlKey || e.metaKey

            // Get next index. If we are jumping to the edge, we move to the first or last row.
            const nextIndex = isJumpToEdge
                ? (isMovingUp ? 0 : rowElements.current.length - 1)
            // Otherwise, we move to the previous or next row.
                : (isMovingUp ? currentRowIndex - 1 : currentRowIndex + 1)

            navigateToRow(nextIndex)
        }

        if (e.key === 'Tab') {
            const findRowIndex = (element: Element) => Array.from(rowElements.current)
                .findIndex(row => row.contains(element) || row === element)

            // Check if we are in header/footer
            if (handleHeaderFooterNavigation(e, target)) return

            const currentCellIndex = cellElements.current.indexOf(currentNavItem)
            // Check if we are in a cell
            if (currentCellIndex !== -1) {
                handleCellNavigation(e, currentNavItem, currentCellIndex)
                return
            }

            const currentRowIndex = findRowIndex(currentNavItem)
            if (currentRowIndex !== -1) {
                handleRowNavigation(e, currentRowIndex)
            }
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    return {
        updateNavList,
        handleKeyDown
    }
}
